<template>
  <aside
    class="fixed left-0 top-0 z-50 h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 print:hidden"
    :class="[
      isCollapsed ? 'w-16' : 'w-64',
      isMobileOpen ? 'translate-x-0' : '-translate-x-full',
      'lg:translate-x-0'
    ]"
  >
    <!-- Logo -->
    <div class="h-16 flex items-center justify-center border-b border-gray-200 dark:border-gray-700">
      <div v-if="isCollapsed" class="w-10 h-10 bg-gray-500 rounded-lg flex items-center justify-center">
        <span class="text-white font-bold text-lg">A</span>
      </div>
      <div v-else class="flex items-center gap-2 px-4">
        <div class="w-10 h-10 bg-gray-500 rounded-lg flex items-center justify-center">
          <span class="text-white font-bold text-lg">J</span>
        </div>
        <div>
          <h1 class="font-bold text-gray-900 dark:text-white">Jackachi House</h1>
          <p class="text-xs text-gray-500 dark:text-gray-400">Admin Panel</p>
        </div>
      </div>
    </div>

    <!-- Navigation -->
    <nav class="p-2 space-y-1">
      <router-link
        v-for="item in navigationItems"
        :key="item.name"
        :to="item.path"
        :class="[
          'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
          isActive(item.path)
            ? 'bg-gray-50 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400'
            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
        ]"
        @click="$emit('close-mobile')"
      >
        <component :is="item.icon" class="w-5 h-5 flex-shrink-0" />
        <span v-if="!isCollapsed" class="text-sm font-medium">{{ item.name }}</span>
        <span
          v-if="!isCollapsed && item.badge"
          class="ml-auto bg-pink-400 text-white text-xs font-bold px-2 py-0.5 rounded-full hidden"
        >
          {{ item.badge }}
        </span>
      </router-link>
    </nav>

    <!-- Bottom Section -->
    <div class="absolute bottom-0 left-0 right-0 p-2 border-t border-gray-200 dark:border-gray-700 space-y-1">
      <!-- Dark Mode Toggle -->
      <button
        :class="[
          'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
          isCollapsed
            ? 'justify-center hover:bg-gray-100 dark:hover:bg-gray-700'
            : 'hover:bg-gray-100 dark:hover:bg-gray-700'
        ]"
        @click="toggleDarkMode"
      >
        <!-- Awesome Toggle Switch -->
        <div class="relative flex-shrink-0">
          <!-- Track -->
          <div
            class="w-12 h-7 rounded-full transition-all duration-300 ease-in-out flex items-center px-1"
            :class="isDark ? 'bg-gray-700 shadow-inner' : 'bg-gradient-to-r from-yellow-300 to-orange-300 shadow-md'"
          >
            <!-- Sun/Moon Icons Background -->
            <div class="absolute inset-0 flex items-center justify-between px-1.5 pointer-events-none">
              <Sun class="w-3.5 h-3.5 text-yellow-600 opacity-70" />
              <Moon class="w-3 h-3 text-gray-400" />
            </div>

            <!-- Sliding Thumb -->
            <div
              class="w-5 h-5 rounded-full shadow-lg transform transition-all duration-300 ease-spring flex items-center justify-center"
              :class="[
                isDark ? 'translate-x-5 bg-gray-800' : 'translate-x-0 bg-white',
                'relative z-10'
              ]"
            >
              <!-- Icon inside thumb -->
              <component
                :is="isDark ? Moon : Sun"
                class="w-3 h-3 transition-colors duration-300"
                :class="isDark ? 'text-gray-300' : 'text-orange-500'"
              />
            </div>
          </div>
        </div>

        <!-- Label -->
        <span v-if="!isCollapsed" class="text-sm font-medium text-gray-600 dark:text-gray-400">
          {{ isDark ? 'Dark Mode' : 'Light Mode' }}
        </span>
      </button>

      <!-- Back to Site Link -->
      <router-link
        to="/"
        :class="[
          'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
        ]"
      >
        <LogOut class="w-5 h-5 flex-shrink-0" />
        <span v-if="!isCollapsed" class="text-sm font-medium">Back to Site</span>
      </router-link>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
import { computed, ref, onMounted } from 'vue'
import {
  LayoutDashboard,
  CalendarDays,
  ClipboardList,
  Package,
  Users,
  Settings,
  LogOut,
  Sun,
  Moon,
  type LucideIcon
} from 'lucide-vue-next'
import { useDarkMode } from '../../composables/useDarkMode'

interface NavigationItem {
  name: string
  path: string
  icon: LucideIcon
}

const props = defineProps<{
  isCollapsed: boolean
  isMobileOpen: boolean
}>()

defineEmits<{
  closeMobile: []
}>()

const route = useRoute()
const { isDark, toggleDarkMode } = useDarkMode()

// User role state
const userRole = ref<string | null>(null)

// Fetch current user role
const fetchCurrentUser = async () => {
  try {
    const response = await fetch('/api/v1/user', {
      credentials: 'include'
    })
    
    if (response.ok) {
      const data = await response.json()
      if (data.user) {
        userRole.value = data.user.role
      }
    }
  } catch (err) {
    console.error('Error fetching user in sidebar:', err)
  }
}

// Fetch user on mount
onMounted(() => {
  fetchCurrentUser()
})

// Navigation items - filtered by role
const navigationItems = computed<NavigationItem[]>(() => {
  // All items for admin/owner/developer
  const allItems: NavigationItem[] = [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
    { name: 'Bookings', path: '/admin/bookings', icon: CalendarDays },
    { name: 'Tasks', path: '/admin/tasks', icon: ClipboardList },
    { name: 'Packages', path: '/admin/packages', icon: Package },
    { name: 'Staffs', path: '/admin/staffs', icon: Users },
    { name: 'Settings', path: '/admin/settings', icon: Settings },
  ]
  
  // Photographer only sees: Dashboard, Tasks, Settings
  if (userRole.value === 'photographer') {
    return allItems.filter(item => 
      ['Dashboard', 'Tasks', 'Settings'].includes(item.name)
    )
  }
  
  return allItems
})

const isActive = (path: string) => {
  if (path === '/admin') {
    return route.path === '/admin'
  }
  return route.path.startsWith(path)
}
</script>

<style scoped>
/* Custom spring easing for the toggle thumb */
.ease-spring {
  transition-timing-function: cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Glow effect for dark mode toggle */
.dark .toggle-glow {
  box-shadow: 0 0 10px rgba(156, 163, 175, 0.3);
}
</style>
